import React, { createContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from 'src/module/user/action';

import {
  SupportedLanguage,
  DEFAULT_LANGUAGE,
  isValidLanguage,
  getTranslation,
  initTranslations
} from './translations';
import { getLanguage } from 'src/module/authentication/selector';
import { Map } from 'immutable';
import { setMomentLocale } from 'src/utils/utils';

const LANGUAGE_STORAGE_KEY = 'app_language';

interface LanguageContextType {
  /** Current language code */
  language: SupportedLanguage;

  /** Change the current language */
  setLanguage: (lang: SupportedLanguage, updateUserPrefs: boolean) => void;

  /** Get translation for a key */
  t: (key: string, variables?: Record<string, string | number>) => string;

  /**
   * Get translation with pluralization support.
   * Uses translation keys with { singular, pl } format.
   * Count is automatically included as {{count}} variable.
   *
   * @example
   * // Translation: 'devices.selected': { singular: '{{count}} Device Selected', pl: '{{count}} Devices Selected' }
   * tPlural('devices.selected', selectedCount)
   * // count=1: "1 Device Selected"
   * // count=5: "5 Devices Selected"
   */
  tPlural: (key: string, count: number, variables?: Record<string, string | number>) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
  tPlural: (key) => key,
});

interface LanguageProviderProps {
  children: ReactNode;
  /** Optional: override the default language */
  defaultLanguage?: SupportedLanguage;
}

/**
 * Detects the user's preferred language from browser settings.
 * Returns the language if supported, otherwise returns the default.
 */
function detectBrowserLanguage (): SupportedLanguage {
  const browserLang = navigator.language?.split('-')[0];

  if (browserLang && isValidLanguage(browserLang)) {
    return browserLang;
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Gets the initial language from:
 * 1. User preferences from Redux state (if provided)
 * 2. localStorage (user's previous preference)
 * 3. Browser language settings
 * 4. Default language
 */
function getInitialLanguage (userLanguage?: SupportedLanguage | null, defaultOverride?: SupportedLanguage): SupportedLanguage {
  // Check user preferences first
  if (userLanguage && isValidLanguage(userLanguage)) {
    return userLanguage;
  }

  // Check localStorage
  const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY) as SupportedLanguage;
  if (storedLang && isValidLanguage(storedLang)) {
    return storedLang;
  }

  // Use override if provided
  if (defaultOverride) {
    return defaultOverride;
  }

  // Try to detect from browser
  return detectBrowserLanguage();
}

export function LanguageProvider ({ children, defaultLanguage }: LanguageProviderProps) {
  // Get user's language preference from Redux state
  const userLanguage = useSelector(getLanguage) as SupportedLanguage | null;

  const [language, setLanguageState] = useState<SupportedLanguage>(() =>
    getInitialLanguage(userLanguage, defaultLanguage)
  );
  const [translationsVersion, setTranslationsVersion] = useState(0);
  const dispatch = useDispatch();
  // Track the last language we set via setLanguage to prevent circular updates
  const lastSetLanguageRef = useRef<SupportedLanguage | null>(null);

  // Sync language with user preference from Redux when it changes
  // Only sync if the change didn't come from our own setLanguage call
  useEffect(() => {
    // Clear the ref if this Redux update matches what we set (prevents circular updates)
    if (lastSetLanguageRef.current && lastSetLanguageRef.current === userLanguage) {
      lastSetLanguageRef.current = null;
      // If languages already match, no need to sync
      if (userLanguage === language) {
        return;
      }
    }

    if (userLanguage && isValidLanguage(userLanguage) && userLanguage !== language) {
      setLanguageState(userLanguage);
      initTranslations(userLanguage).then(() => {
        setTranslationsVersion(v => v + 1);
      });
      localStorage.setItem(LANGUAGE_STORAGE_KEY, userLanguage);
      document.documentElement.lang = userLanguage;
    }
  }, [userLanguage, language]);

  // Initialize translations on mount
  useEffect(() => {
    initTranslations(language).then(() => {
      setTranslationsVersion(v => v + 1);
    });
  }, []);

  // Change language and load new translations
  const setLanguage = useCallback(async (lang: SupportedLanguage, updateUserPrefs: boolean = false) => {
    // Track that we're setting this language to prevent circular updates
    if (updateUserPrefs) {
      lastSetLanguageRef.current = lang;
    }
    await initTranslations(lang);
    setLanguageState(lang);
    setTranslationsVersion(v => v + 1); // Force re-render after translations loaded
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    if (updateUserPrefs) {
      dispatch(updateUser(null, Map({ language: lang })));
    }
    // Update the HTML lang attribute
    document.documentElement.lang = lang;
  }, [dispatch]);

  // Set initial HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Set moment locale whenever language changes
  useEffect(() => {
    setMomentLocale(language);
  }, [language]);

  // Translation function - depends on both language and translationsVersion to ensure re-render
  const t = useCallback((key: string, variables?: Record<string, string | number>): string => {
    return getTranslation(key, variables ? { variables } : undefined);
  }, [language, translationsVersion]);

  // Pluralized translation function
  const tPlural = useCallback((
    key: string,
    count: number,
    variables?: Record<string, string | number>
  ): string => {
    return getTranslation(key, { count, variables });
  }, [language, translationsVersion]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    tPlural,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
